import { useMemo } from 'react';
import { Admin, Resource, CustomRoutes } from 'react-admin';
import { Route, BrowserRouter, Routes, useParams, useNavigate } from 'react-router-dom';
import { devLog } from './utils/devLog';
import authProvider from './authProvider';
import dataProvider from './ssoDataProvider';
import AuthCallback from './AuthCallback';
import LoginPage from './LoginPage';
import CustomLayout from './Layout';
import ChangePassword from './ChangePassword';
import Profile from './Profile';
import PageTitle from './PageTitle';
import { createDynamicTheme } from './theme';
import { i18nProvider } from './i18nProvider';
import { BrandingProvider, useBranding } from './contexts/BrandingContext';
import TranslateHelper from './TranslateHelper';
import Dashboard from './Dashboard';
import { LearningPathList, LearningPathEdit, LearningPathCreate } from './resources/learningPaths';
import { BadgeList, BadgeEdit, BadgeCreate } from './resources/badges';
import { CoursePrerequisiteList, CoursePrerequisiteEdit, CoursePrerequisiteCreate } from './resources/coursePrerequisites';
import MediaLibrary from './components/MediaLibrary/MediaLibrary';
import Wiki from './components/Wiki/Wiki';
import WikiEditor from './components/Wiki/WikiEditor';
import WikiCategories from './components/Wiki/WikiCategories';
import WikiViewer from './components/Wiki/WikiViewer';
import CourseCatalogs from './components/Courses/CourseCatalogs';
import CatalogCourses from './components/Courses/CatalogCourses';
import CourseLessons from './components/Courses/CourseLessons';
import RequireRole from './components/auth/RequireRole';
import RequireFeature from './components/auth/RequireFeature';
import LessonContentEditor from './components/Courses/LessonContentEditor';
import EnrollmentManagement from './components/Enrollments/EnrollmentManagement';
import MyCourses from './components/Enrollments/MyCourses';
import CourseCatalog from './components/CourseCatalog/CourseCatalog';
import EnrollmentDashboard from './components/Enrollments/EnrollmentDashboard';
import CoursePlayer from './components/CoursePlayer/CoursePlayer';
import { MyCertificates, VerifyPage } from './components/Certificates';
import { LearningPathsPage, LearningPathDetail } from './components/LearningPaths';
import { MyAchievements } from './components/Gamification';
import { BookmarkedCourses } from './components/Bookmarks';
import { SearchResults } from './components/Search';
import { AnalyticsDashboard, InstructorAnalytics } from './components/Analytics';
import { GroupList, GroupDetail } from './components/LearningGroups';
import { PeerReviewList, PeerReviewForm, PeerReviewResult } from './components/PeerReview';
import { FaqPage } from './components/FAQ';
import { ContentLibrary } from './components/Library';
import { NotesOverview } from './components/Notes';
import AiGradingReview from './components/AiReview/AiGradingReview';
import { CartPage, CartProvider } from './components/Cart';
import { CheckoutPage } from './components/Checkout';
import { MyOrders } from './components/Orders';
import { PublicLandingPage, GuestCheckout } from './components/LandingPage';
// Aus dem Admin verschoben (LMS-spezifische Verwaltung — gehört in die App)
import { CourseAssignmentList, CourseAssignmentCreate, CourseAssignmentEdit, AssignmentIcon } from './resources/courseAssignments';
import OrdersPage from './pages/OrdersPage';
// Aus dem Admin verschoben (2026-09, azubi:web-Feedback): Einladungen +
// Branding haendelt jede App selbst; das eigene Profil lebt nur noch hier.
import { InvitationList, InvitationShow, InvitationCreate } from './resources/invitations';
import RegisterPage from './RegisterPage';
import BrandingSettings from './components/Settings/BrandingSettings';
import MailIcon from '@mui/icons-material/Mail';
import { tokenService } from './services/tokenService';
import LandingPagesPage from './pages/LandingPagesPage';
import ProductsPage from './pages/ProductsPage';
import MarketplacePage from './pages/MarketplacePage';
import LicensedCoursesPage from './pages/LicensedCoursesPage';
import TemplatesPage from './pages/TemplatesPage';
import ManagementAnalyticsPage from './pages/AnalyticsPage';
import PaymentSettings from './components/Settings/PaymentSettings';
import RouteIcon from '@mui/icons-material/Route';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LockIcon from '@mui/icons-material/Lock';

// Initialize auto-login flag BEFORE React Admin starts
// Always set auto_login so checkAuth can redirect to SSO immediately
// (avoids the slow checkAuth → logout → LoginPage → login cycle)
if (window.location.pathname !== '/auth-callback') {
    localStorage.setItem('auto_login_in_progress', 'true');
    localStorage.removeItem('user_logged_out');
} else {
    localStorage.removeItem('auto_login_in_progress');
}

// Route wrapper for LearningPathDetail (expects pathId prop, not useParams)
const LearningPathDetailRoute = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    return (
        <LearningPathDetail
            pathId={parseInt(id || '0')}
            onBack={() => navigate('/learning-paths')}
            onCourseSelect={(courseId) => navigate(`/course-player/${courseId}`)}
        />
    );
};

// Inner component - can use useBranding() because it's rendered inside BrandingProvider
const AppContent = () => {
    const { branding, companyName } = useBranding();
    const dynamicTheme = useMemo(() => createDynamicTheme(branding), [branding]);
    const appTitle = branding.appTitle || companyName || 'Online Academy';
    // Einladungs-Resource nur fuer Company-Level-Rollen registrieren —
    // gleiche Schwelle wie das Verwaltungs-Menue im Layout.
    const userRoles = tokenService.getRoles();
    const isCompanyLevelUser = ['ROLE_COMPANY_ADMIN', 'ROLE_PLATFORM_ADMIN', 'ROLE_OPERATOR']
        .some((role) => userRoles.includes(role));

    return (
        <CartProvider>
        <Admin
            dataProvider={dataProvider}
            authProvider={authProvider}
            i18nProvider={i18nProvider}
            loginPage={LoginPage}
            layout={CustomLayout}
            theme={dynamicTheme}
            title={appTitle}
            dashboard={Dashboard}
            requireAuth
        >
        <PageTitle />

        {/* Medienverwaltung */}
        <Resource name="media_files" />
        <Resource name="media_folders" />
        <Resource name="media_categories" />

        {/* Wiki Resources - needed for dataProvider */}
        <Resource name="wiki_articles" />
        <Resource name="wiki_categories" />
        <Resource name="wiki_versions" />
        <Resource name="wiki_comments" />
        <Resource name="wiki_favorites" />

        {/* Course Resources - needed for dataProvider */}
        <Resource name="course_catalogs" />
        <Resource name="courses" />
        <Resource name="lessons" />
        <Resource name="course_enrollments" />
        <Resource name="lesson_progress" />
        <Resource name="lesson_attempts" />

        {/* Learning Path Resources */}
        <Resource
            name="learning_paths"
            list={LearningPathList}
            edit={LearningPathEdit}
            create={LearningPathCreate}
            icon={RouteIcon}
            options={{ label: 'Lernpfade' }}
        />
        <Resource name="learning_path_courses" />
        <Resource name="learning_path_enrollments" />

        {/* Gamification Resources */}
        <Resource
            name="badges"
            list={BadgeList}
            edit={BadgeEdit}
            create={BadgeCreate}
            icon={EmojiEventsIcon}
            options={{ label: 'Auszeichnungen' }}
        />
        <Resource name="user_badges" />
        <Resource name="user_streaks" />

        {/* Prerequisites */}
        <Resource
            name="course_prerequisites"
            list={CoursePrerequisiteList}
            edit={CoursePrerequisiteEdit}
            create={CoursePrerequisiteCreate}
            icon={LockIcon}
            options={{ label: 'Voraussetzungen' }}
        />

        {/* Pflichtaufgaben — aus dem Admin verschoben */}
        <Resource
            name="course_assignments"
            list={CourseAssignmentList}
            create={CourseAssignmentCreate}
            edit={CourseAssignmentEdit}
            icon={AssignmentIcon}
            options={{ label: 'Pflichtaufgaben' }}
        />
        {/* Bare Resource für Referenzen (Reminder-Vorlagen, Vorlagen-Seite) */}
        <Resource name="document_templates" />

        {/* Einladungen — aus dem Admin verschoben (2026-09). Nur für
            Company-Level-Rollen registriert; companies/roles sind bare
            Resources für die Referenz-Inputs (Routing zum SSO im
            ssoDataProvider/ssoCrudDataProvider). */}
        {isCompanyLevelUser && (
            <Resource
                name="invitations"
                list={InvitationList}
                show={InvitationShow}
                create={InvitationCreate}
                icon={MailIcon}
                options={{ label: 'Einladungen' }}
            />
        )}
        <Resource name="companies" />
        <Resource name="roles" />

        <CustomRoutes>
            <Route path="/change-password" element={<ChangePassword />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/translate" element={<TranslateHelper />} />
            <Route path="/media-library" element={<MediaLibrary />} />
            <Route path="/wiki" element={<Wiki />} />
            <Route path="/wiki/categories" element={<WikiCategories />} />
            <Route path="/wiki/:slug/edit" element={
                <RequireFeature feature="wiki.edit" redirectTo="/wiki">
                    <WikiEditor />
                </RequireFeature>
            } />
            <Route path="/wiki/:slug" element={<WikiViewer />} />
            {/* Issue #153: alle /courses/*-Routen sind die Kursverwaltung — Studenten
                duerfen sie nicht sehen, weder via Direktlink noch ueber das Burger-Menu.
                Sie werden auf den Lerner-Katalog (/catalog) umgeleitet. */}
            <Route path="/courses/catalogs" element={
                <RequireRole allow={['ROLE_INSTRUCTOR', 'ROLE_CONTENT_MANAGER', 'ROLE_COMPANY_ADMIN', 'ROLE_PLATFORM_ADMIN', 'ROLE_OPERATOR']} redirectTo="/catalog">
                    <CourseCatalogs />
                </RequireRole>
            } />
            <Route path="/courses/catalog/:catalogId" element={
                <RequireRole allow={['ROLE_INSTRUCTOR', 'ROLE_CONTENT_MANAGER', 'ROLE_COMPANY_ADMIN', 'ROLE_PLATFORM_ADMIN', 'ROLE_OPERATOR']} redirectTo="/catalog">
                    <CatalogCourses />
                </RequireRole>
            } />
            <Route path="/courses/:courseId/lessons" element={
                <RequireRole allow={['ROLE_INSTRUCTOR', 'ROLE_CONTENT_MANAGER', 'ROLE_COMPANY_ADMIN', 'ROLE_PLATFORM_ADMIN', 'ROLE_OPERATOR']} redirectTo="/catalog">
                    <CourseLessons />
                </RequireRole>
            } />
            <Route path="/lesson/:lessonId/content" element={
                <RequireRole allow={['ROLE_INSTRUCTOR', 'ROLE_CONTENT_MANAGER', 'ROLE_COMPANY_ADMIN', 'ROLE_PLATFORM_ADMIN', 'ROLE_OPERATOR']} redirectTo="/catalog">
                    <LessonContentEditor />
                </RequireRole>
            } />
            <Route path="/library" element={<ContentLibrary />} />
            <Route path="/enrollments/manage" element={
                <RequireFeature feature="course.manage_enrollments" redirectTo="/">
                    <EnrollmentManagement />
                </RequireFeature>
            } />
            <Route path="/enrollments/dashboard" element={
                <RequireFeature feature="course.manage_enrollments" redirectTo="/">
                    <EnrollmentDashboard />
                </RequireFeature>
            } />
            <Route path="/catalog" element={<CourseCatalog />} />
            <Route path="/my-courses" element={<MyCourses />} />
            <Route path="/my-certificates" element={<MyCertificates />} />
            <Route path="/certificates" element={<MyCertificates />} />
            <Route path="/course-player/:courseId" element={<CoursePlayer />} />
            <Route path="/learning-paths" element={
                <RequireFeature feature="learning_path.view" redirectTo="/">
                    <LearningPathsPage />
                </RequireFeature>
            } />
            <Route path="/learning-paths/:id" element={
                <RequireFeature feature="learning_path.view" redirectTo="/">
                    <LearningPathDetailRoute />
                </RequireFeature>
            } />
            <Route path="/achievements" element={<MyAchievements />} />
            <Route path="/bookmarks" element={<BookmarkedCourses />} />
            <Route path="/search" element={<SearchResults />} />
            <Route path="/analytics" element={
                <RequireFeature feature="reports.analytics" redirectTo="/">
                    <AnalyticsDashboard />
                </RequireFeature>
            } />
            <Route path="/analytics/instructor" element={
                <RequireFeature feature="reports.analytics" redirectTo="/">
                    <InstructorAnalytics />
                </RequireFeature>
            } />
            {/* Social Learning */}
            <Route path="/learning-groups" element={<GroupList />} />
            <Route path="/learning-groups/:id" element={<GroupDetail />} />
            <Route path="/peer-review" element={<PeerReviewList />} />
            <Route path="/peer-review/:id" element={<PeerReviewForm />} />
            <Route path="/peer-review/:id/result" element={<PeerReviewResult />} />
            {/* Issue #197 (EU-KI-Act): Trainer-Review der KI-Bewertungen */}
            <Route path="/ai-grading-review" element={
                <RequireRole allow={['ROLE_INSTRUCTOR', 'ROLE_CONTENT_MANAGER', 'ROLE_COMPANY_ADMIN', 'ROLE_PLATFORM_ADMIN', 'ROLE_OPERATOR']} redirectTo="/">
                    <AiGradingReview />
                </RequireRole>
            } />
            {/* Shopping */}
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/my-orders" element={<MyOrders />} />
            {/* Notes */}
            <Route path="/notes" element={<NotesOverview />} />
            {/* Verwaltung — aus dem Admin verschoben (LMS-spezifisch).
                Rollen-Gating analog zum bisherigen Admin-Menü. */}
            <Route path="/templates" element={
                <RequireRole allow={['ROLE_CONTENT_MANAGER', 'ROLE_COMPANY_ADMIN', 'ROLE_PLATFORM_ADMIN', 'ROLE_OPERATOR']} redirectTo="/">
                    <TemplatesPage />
                </RequireRole>
            } />
            <Route path="/orders" element={
                <RequireRole allow={['ROLE_CONTENT_MANAGER', 'ROLE_COMPANY_ADMIN', 'ROLE_PLATFORM_ADMIN', 'ROLE_OPERATOR']} redirectTo="/">
                    <OrdersPage />
                </RequireRole>
            } />
            <Route path="/orders/:id" element={
                <RequireRole allow={['ROLE_CONTENT_MANAGER', 'ROLE_COMPANY_ADMIN', 'ROLE_PLATFORM_ADMIN', 'ROLE_OPERATOR']} redirectTo="/">
                    <OrdersPage />
                </RequireRole>
            } />
            <Route path="/products" element={
                <RequireRole allow={['ROLE_CONTENT_MANAGER', 'ROLE_COMPANY_ADMIN', 'ROLE_PLATFORM_ADMIN', 'ROLE_OPERATOR']} redirectTo="/">
                    <ProductsPage />
                </RequireRole>
            } />
            <Route path="/landing-pages" element={
                <RequireRole allow={['ROLE_CONTENT_MANAGER', 'ROLE_COMPANY_ADMIN', 'ROLE_PLATFORM_ADMIN', 'ROLE_OPERATOR']} redirectTo="/">
                    <LandingPagesPage />
                </RequireRole>
            } />
            <Route path="/marketplace" element={
                <RequireRole allow={['ROLE_CONTENT_MANAGER', 'ROLE_COMPANY_ADMIN', 'ROLE_PLATFORM_ADMIN', 'ROLE_OPERATOR']} redirectTo="/">
                    <MarketplacePage />
                </RequireRole>
            } />
            <Route path="/licensed-courses" element={
                <RequireRole allow={['ROLE_COMPANY_ADMIN', 'ROLE_PLATFORM_ADMIN', 'ROLE_OPERATOR']} redirectTo="/">
                    <LicensedCoursesPage />
                </RequireRole>
            } />
            <Route path="/payment-settings" element={
                <RequireRole allow={['ROLE_COMPANY_ADMIN', 'ROLE_PLATFORM_ADMIN', 'ROLE_OPERATOR']} redirectTo="/">
                    <PaymentSettings />
                </RequireRole>
            } />
            {/* Branding — aus dem Admin verschoben (2026-09, azubi:web-Feedback) */}
            <Route path="/branding-settings" element={
                <RequireRole allow={['ROLE_COMPANY_ADMIN', 'ROLE_PLATFORM_ADMIN', 'ROLE_OPERATOR']} redirectTo="/">
                    <BrandingSettings />
                </RequireRole>
            } />
            {/* Manager-Analytics (reiche Auswertung aus dem Admin verschoben) —
                getrennt von der Lerner-/Instructor-Analytics unter /analytics. */}
            <Route path="/analytics-management" element={
                <RequireRole allow={['ROLE_CONTENT_MANAGER', 'ROLE_COMPANY_ADMIN', 'ROLE_PLATFORM_ADMIN', 'ROLE_OPERATOR']} redirectTo="/">
                    <RequireFeature feature="reports.analytics" redirectTo="/">
                        <ManagementAnalyticsPage />
                    </RequireFeature>
                </RequireRole>
            } />
        </CustomRoutes>
        </Admin>
        </CartProvider>
    );
};

const App = () => {
    devLog('[App] Rendering, pathname:', window.location.pathname);

    // Handle auth callback outside of React Admin to avoid auth checks
    if (window.location.pathname === '/auth-callback') {
        devLog('[App] Rendering AuthCallback');
        return <AuthCallback />;
    }

    // Handle public certificate verification without auth
    if (window.location.pathname.startsWith('/verify/')) {
        devLog('[App] Rendering VerifyPage');
        return (
            <BrowserRouter>
                <Routes>
                    <Route path="/verify/:code" element={<VerifyPage />} />
                </Routes>
            </BrowserRouter>
        );
    }

    // Handle public FAQ page without auth
    if (window.location.pathname === '/faq') {
        devLog('[App] Rendering FaqPage');
        return (
            <BrowserRouter>
                <Routes>
                    <Route path="/faq" element={<FaqPage />} />
                </Routes>
            </BrowserRouter>
        );
    }

    // Public invitation redemption (Einladungs-Mails verlinken hierher) —
    // ohne Auth, analog zu /verify und /faq. Aus dem Admin verschoben (2026-09).
    if (window.location.pathname === '/register') {
        devLog('[App] Rendering RegisterPage');
        return (
            <BrowserRouter>
                <Routes>
                    <Route path="/register" element={<RegisterPage />} />
                </Routes>
            </BrowserRouter>
        );
    }

    // Handle public landing pages without auth
    if (window.location.pathname.startsWith('/p/')) {
        devLog('[App] Rendering PublicLandingPage');
        return (
            <BrowserRouter>
                <Routes>
                    <Route path="/p/:slug" element={<PublicLandingPage />} />
                    <Route path="/p/:slug/checkout" element={<GuestCheckout />} />
                </Routes>
            </BrowserRouter>
        );
    }

    devLog('[App] Rendering Admin');

    return (
        <BrandingProvider>
            <AppContent />
        </BrandingProvider>
    );
};

export default App;
